import { createI18n } from 'vue-i18n';
import en from '@/i18n/messages/en/index.ts';
import nl from '@/i18n/messages/nl/index.ts';

/**
 * The admin's own UI language (not the website's content languages, which
 * are per website and edited through LocaleTabs).
 *
 * Each user picks it on the Profile page; it is stored on their account
 * (`locale` on /me) so it follows them to every device. Never picked = the
 * browser's language when we have it, else English. Before login there is
 * no account yet, so the login screen uses the last language this browser
 * showed.
 *
 * Adding a language: one folder under messages/ with every namespace file
 * (the `satisfies Translation<...>` in each makes a missing or extra key a
 * type error), plus one entry in LOCALES. Nothing changes on the server.
 *
 * In code: `$t('ns.key')` in templates, `const { t } = useI18n()` in a
 * component's script, and the `t` exported here anywhere else. Never call
 * t() at module top level: the result would be frozen in whatever language
 * was active when the module loaded.
 */

export const LOCALES = [
    { code: 'en', name: 'English', intl: 'en-GB' },
    { code: 'nl', name: 'Nederlands', intl: 'nl-NL' },
] as const;

export type UiLocale = (typeof LOCALES)[number]['code'];

export type MessageSchema = typeof en;

const STORAGE_KEY = 'cms-admin.locale';
const FALLBACK: UiLocale = 'en';

export function isUiLocale(code: unknown): code is UiLocale {
    return typeof code === 'string' && LOCALES.some((l) => l.code === code);
}

/** The first browser language we have messages for, else English. */
export function browserLocale(): UiLocale {
    const langs = typeof navigator === 'undefined' ? [] : (navigator.languages ?? [navigator.language]);
    for (const lang of langs) {
        const code = lang?.slice(0, 2).toLowerCase();
        if (isUiLocale(code)) return code;
    }
    return FALLBACK;
}

function storedLocale(): UiLocale | null {
    try {
        const v = localStorage.getItem(STORAGE_KEY);
        return isUiLocale(v) ? v : null;
    } catch {
        return null;
    }
}

export const i18n = createI18n<[MessageSchema], UiLocale, false>({
    legacy: false,
    locale: storedLocale() ?? browserLocale(),
    fallbackLocale: FALLBACK,
    messages: { en, nl },
    // A missing key is a bug the i18n check script catches before build;
    // in the console it is only noise during development.
    missingWarn: false,
    fallbackWarn: false,
});

/** Translate outside a component (stores, lib modules, router guards). */
export const t = i18n.global.t;

export function currentLocale(): UiLocale {
    return i18n.global.locale.value;
}

/** BCP 47 tag for Intl formatters: en-GB / nl-NL. Read it at format time, not at module load. */
export function intlLocale(): string {
    return LOCALES.find((l) => l.code === currentLocale())?.intl ?? 'en-GB';
}

/** Show the admin in `code` now, and remember it for this browser's login screen. */
export function setLocale(code: UiLocale): void {
    i18n.global.locale.value = code;
    document.documentElement.lang = code;
    try {
        localStorage.setItem(STORAGE_KEY, code);
    } catch {
        // Private mode or blocked storage: the login screen falls back to the browser.
    }
}

/**
 * Apply the signed-in user's choice from /me. Null (never picked) or a code
 * we have no messages for follows the browser.
 */
export function applyUserLocale(code: string | null | undefined): void {
    setLocale(isUiLocale(code) ? code : browserLocale());
}
